// src/feed/setShareEnabled.ts
//
// Toggle the share_enabled flag on a feed_item. When set to true,
// the item becomes readable by anyone (auth or not) via the
// /post/{itemId} public URL — bypassing the visibility model
// without changing it. Revoking flips the flag back to false; the
// underlying visibility (private/followers/public) is preserved
// throughout, so revocation returns the item to its original
// audience without manual reconfiguration.
//
// All writes go through the standard Firestore update path and
// inherit the existing rule that owners may only update their own
// items.

import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../services/firebase';

/** Compose the public share URL for a feed item. Always uses the
 *  production origin in production, falling back to window.location.origin
 *  in dev/staging so links work locally without rewrites. */
export function buildShareUrl(itemId: string): string {
  const origin =
    typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : 'https://instant.tours';
  return `${origin}/post/${itemId}`;
}

export async function setFeedItemShareEnabled(
  itemId: string,
  enabled: boolean
): Promise<void> {
  const ref = doc(db, 'feed_items', itemId);
  await updateDoc(ref, {
    share_enabled: enabled,
    updated_at: serverTimestamp(),
  });
}

/** Convenience wrapper that flips the flag on (no-op if already on)
 *  and triggers the platform share sheet, falling back to clipboard
 *  copy when navigator.share isn't available (most desktop browsers).
 *  Returns the URL that was shared/copied so callers can show a
 *  toast or display it. */
export async function shareFeedItem(opts: {
  itemId: string;
  alreadyEnabled: boolean;
  title?: string;
  text?: string;
}): Promise<{ url: string; method: 'native' | 'clipboard' | 'unsupported' }> {
  const { itemId, alreadyEnabled, title, text } = opts;
  if (!alreadyEnabled) {
    await setFeedItemShareEnabled(itemId, true);
  }
  const url = buildShareUrl(itemId);

  // Native share sheet on platforms that support it (iOS Safari,
  // Android Chrome, some desktop builds). Falls through to
  // clipboard otherwise. We catch user-cancellation (AbortError)
  // separately because that's not really a failure.
  if (typeof navigator !== 'undefined' && (navigator as any).share) {
    try {
      await (navigator as any).share({
        url,
        title: title || 'Instant Tours',
        text: text || '',
      });
      return { url, method: 'native' };
    } catch (err: any) {
      if (err?.name === 'AbortError') {
        // User dismissed the share sheet — not an error.
        return { url, method: 'native' };
      }
      // Fall through to clipboard
    }
  }

  if (
    typeof navigator !== 'undefined' &&
    navigator.clipboard?.writeText
  ) {
    try {
      await navigator.clipboard.writeText(url);
      return { url, method: 'clipboard' };
    } catch {
      // Clipboard denied — caller can fall back to a manual prompt.
    }
  }

  return { url, method: 'unsupported' };
}
